import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Cookie, X, Lock, BarChart3 } from "lucide-react";

const STORAGE_KEY = "cookie_consent";
const LEGACY_KEY = "flowbtp_cookie_consent_v1";

export function getCookieConsent() {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) || localStorage.getItem(LEGACY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeConsent({ analytics }) {
  const payload = {
    essentials: true,
    analytics: !!analytics,
    decided_at: new Date().toISOString(),
    version: 2,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  // remove legacy key so only one source of truth remains
  localStorage.removeItem(LEGACY_KEY);
  return payload;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) setVisible(true);
  }, []);

  const acceptAll = () => {
    writeConsent({ analytics: true });
    setCustomizeOpen(false);
    setVisible(false);
  };

  const rejectAll = () => {
    writeConsent({ analytics: false });
    setCustomizeOpen(false);
    setVisible(false);
  };

  const savePrefs = () => {
    writeConsent({ analytics: analyticsChecked });
    setCustomizeOpen(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      <div
        role="dialog"
        aria-live="polite"
        aria-label="Bandeau de consentement aux cookies"
        data-testid="cookie-consent-banner"
        /* BUG-039 : se poser AU-DESSUS de la barre de navigation du bas quand
           elle existe, au lieu de la recouvrir. `--cf-bottom-nav-h` est posee
           par BottomNav pendant qu'elle est montee, et vaut 0 ailleurs — sur
           les pages publiques le bandeau reste donc au ras du bas.

           C'est `bottom` qu'il faut decaler, pas le remplissage : le conteneur
           est en `fixed`, un `padding-bottom` remonte la carte mais laisse la
           zone d'interception descendre jusqu'en bas. Premiere tentative faite,
           et la mesure a montre « Accueil » et « Chantiers » toujours
           recouverts — par le conteneur, cette fois. */
        className="fixed inset-x-0 z-[60] px-3 pb-3 sm:px-6 sm:pb-6"
        style={{ bottom: "var(--cf-bottom-nav-h, 0px)" }}
      >
        <div className="mx-auto max-w-4xl rounded-xl border border-border bg-card shadow-xl">
          <div className="flex items-start gap-3 p-4 sm:p-5">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand/10 text-brand">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1 text-sm text-foreground leading-relaxed">
              <p className="font-semibold text-foreground mb-1">
                Votre vie privée nous tient à cœur
              </p>
              <p>
                KNOWROK utilise des cookies strictement nécessaires au fonctionnement du service.
                Vous pouvez aussi accepter des cookies de mesure d'audience pour nous aider à
                l'améliorer. En savoir plus dans notre{" "}
                <Link
                  to="/legal/cookies"
                  className="text-brand underline underline-offset-2 hover:text-brand/80"
                >
                  politique cookies
                </Link>
                .
              </p>
            </div>
            {/* 24 x 24 mesuré : c'est la croix qui refuse TOUT le pistage, et
                elle était la plus petite cible de l'écran. Le dessin reste à
                16 px, seule la zone cliquable passe à 44 sur mobile. */}
            <button
              onClick={rejectAll}
              aria-label="Refuser tout et fermer"
              className="shrink-0 rounded-md text-muted-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand/40 h-11 w-11 md:h-8 md:w-8 inline-flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch gap-2 border-t border-border p-3 sm:p-4 sm:justify-end">
            {/* 85 x 16 mesuré. Souligné comme un lien, mais c'est une COMMANDE
                isolée dans une rangée de boutons — pas un lien au fil d'une
                phrase, donc l'exception « en ligne » de WCAG 2.5.5 ne s'y
                applique pas. Refuser doit être aussi facile à viser
                qu'accepter. */}
            <button
              onClick={rejectAll}
              data-testid="cookie-consent-reject"
              className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 self-center sm:mr-auto px-2 min-h-[44px] md:min-h-0"
            >
              Refuser tout
            </button>
            <Button
              data-testid="cookie-consent-customize"
              variant="outline"
              onClick={() => {
                setAnalyticsChecked(false);
                setCustomizeOpen(true);
              }}
            >
              Personnaliser
            </Button>
            <Button
              data-testid="cookie-consent-accept"
              className="bg-primary hover:bg-brand/90"
              onClick={acceptAll}
            >
              Accepter
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent
          data-testid="cookie-consent-dialog"
          className="sm:max-w-lg"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="h-5 w-5 text-brand" /> Préférences cookies
            </DialogTitle>
            <DialogDescription>
              Choisissez quels cookies vous acceptez. Les cookies essentiels ne peuvent pas être
              désactivés.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-muted p-4"
              data-testid="cookie-category-essentials"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Lock className="h-4 w-4 text-muted-foreground" />
                  <p className="font-semibold text-foreground">Cookies essentiels</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Nécessaires au fonctionnement du site (authentification, sécurité).
                  Impossible à désactiver.
                </p>
              </div>
              <Switch
                checked
                disabled
                data-testid="cookie-toggle-essentials"
                aria-label="Cookies essentiels (verrouillé)"
              />
            </div>

            <div
              className="flex items-start justify-between gap-4 rounded-lg border border-border p-4"
              data-testid="cookie-category-analytics"
            >
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  <p className="font-semibold text-foreground">Analyse d'usage</p>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Nous aide à comprendre comment KNOWROK est utilisé pour l'améliorer.
                  Aucune donnée publicitaire.
                </p>
              </div>
              <Switch
                checked={analyticsChecked}
                onCheckedChange={setAnalyticsChecked}
                data-testid="cookie-toggle-analytics"
                aria-label="Cookies d'analyse d'usage"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setCustomizeOpen(false)}
              data-testid="cookie-consent-cancel"
            >
              Annuler
            </Button>
            <Button
              className="bg-primary hover:bg-brand/90"
              onClick={savePrefs}
              data-testid="cookie-consent-save"
            >
              Enregistrer mes préférences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
